import React, {
  cloneElement,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { tv } from 'tailwind-variants';

import { cls } from '@/utils/cls';

export type StatsBoxProps = {
  className?: string;
  icon: {
    color?: 'yellow' | 'red' | 'green' | 'neutral' | 'primary';
    element: ReactElement<{ className?: string }>;
  };
  title: string;
  value: number | string | React.ReactNode;
};
export function StatsBox({
  className,
  icon: { color = 'neutral', element },
  title,
  value,
  children,
}: React.PropsWithChildren<StatsBoxProps>) {
  const style = statsBoxStyle({ color });
  const boxRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState('text-2xl');

  useEffect(function () {
    if (!valueRef.current || !boxRef.current) {
      return;
    }

    const overflow = valueRef.current.offsetWidth - boxRef.current.offsetWidth;
    if (overflow > 0) {
      if (overflow > 40) {
        setFontSize('text-lg');
      } else {
        setFontSize('text-xl');
      }
    }
  }, []);

  return (
    <div
      className={cls(
        'relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6',
        className,
      )}
    >
      <dt>
        <div className={style.iconBox()}>
          {cloneElement(element, {
            className: style.icon(),
          })}
        </div>
        <p className="ml-16 truncate text-sm font-medium text-gray-500">
          {title}
        </p>
      </dt>
      <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
        <div
          ref={boxRef}
          className="w-full overflow-x-auto overflow-y-hidden font-semibold text-gray-900"
        >
          <span ref={valueRef} className={cls('whitespace-nowrap', fontSize)}>
            {value}
          </span>
        </div>
        {children}
      </dd>
    </div>
  );
}

type StatsBoxActionProps = {};
StatsBox.Action = function StatsBoxAction({
  children,
}: React.PropsWithChildren<StatsBoxActionProps>) {
  return (
    <div className="absolute inset-x-0 bottom-0 bg-gray-50 p-4 sm:px-6">
      <div className="text-sm font-medium text-primary-500 hover:text-primary-600">
        {children ?? <>&nbsp;</>}
      </div>
    </div>
  );
};

const statsBoxStyle = tv({
  slots: {
    iconBox: 'absolute rounded-md p-3',
    icon: 'size-6',
  },
  variants: {
    color: {
      green: {
        iconBox: 'bg-green-100',
        icon: 'text-green-500',
      },
      yellow: {
        iconBox: 'bg-yellow-100',
        icon: 'text-yellow-500',
      },
      red: {
        iconBox: 'bg-red-100',
        icon: 'text-red-500',
      },
      neutral: {
        iconBox: 'bg-neutral-100',
        icon: 'text-neutral-500',
      },
      primary: {
        iconBox: 'bg-primary-50',
        icon: 'text-primary-500',
      },
    },
  },
});
